"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Junction = exports.junctionAttributesWithComposedAttributes = exports.junctionAttributes = void 0;
const omit_1 = __importDefault(require("lodash/omit"));
const IValidatable_1 = require("./IValidatable");
const CompletableAttributes_1 = require("./attributeTypes/CompletableAttributes");
const SurveyObject_1 = require("./SurveyObject");
const Weight_1 = require("./Weight");
const Uuidable_1 = require("./Uuidable");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
const Place_1 = require("./Place");
const Result_type_1 = require("../../types/Result.type");
const StartEndable_1 = require("./StartEndable");
const ConstructorUtils_1 = require("../../utils/ConstructorUtils");
const SurveyObjectUnserializer_1 = require("./SurveyObjectUnserializer");
exports.junctionAttributes = [
    ...StartEndable_1.startEndDateAndTimesAttributes,
    ...Weight_1.weightableAttributeNames,
    ...IValidatable_1.validatableAttributeNames,
    ...Uuidable_1.uuidableAttributeNames,
    ...CompletableAttributes_1.completableAttributeNames,
    'parkingType',
    'parkingFeeType',
    'transitPlaceType',
    'preData'
];
exports.junctionAttributesWithComposedAttributes = [...exports.junctionAttributes, 'place'];
/**
 * A junction is a place used to transfer between segments/modes by a person during a trip
 * Usually, junctions are used as origin and/or destination for segments
 * Junctions are optional in most surveys
 */
class Junction extends SurveyObject_1.SurveyObject {
    constructor(params, surveyObjectsRegistry) {
        super(params._uuid);
        this._surveyObjectsRegistry = surveyObjectsRegistry;
        this._attributes = {};
        this._customAttributes = {};
        const { attributes, customAttributes } = ConstructorUtils_1.ConstructorUtils.initializeAttributes((0, omit_1.default)(params, ['_place', 'place', '_tripUuid']), exports.junctionAttributes, exports.junctionAttributesWithComposedAttributes);
        this._attributes = attributes;
        this._customAttributes = customAttributes;
        this.place = ConstructorUtils_1.ConstructorUtils.initializeComposedAttribute(params._place, (params) => Place_1.Place.unserialize(params, this._surveyObjectsRegistry), this._surveyObjectsRegistry);
        this.tripUuid = params._tripUuid;
        this._surveyObjectsRegistry.registerJunction(this);
    }
    get attributes() {
        return this._attributes;
    }
    get customAttributes() {
        return this._customAttributes;
    }
    get _weights() {
        return this._attributes._weights;
    }
    set _weights(value) {
        this._attributes._weights = value;
    }
    get place() {
        return this._place;
    }
    set place(value) {
        this._place = value;
    }
    get startDate() {
        return this._attributes.startDate;
    }
    set startDate(value) {
        this._attributes.startDate = value;
    }
    get startTime() {
        return this._attributes.startTime;
    }
    set startTime(value) {
        this._attributes.startTime = value;
    }
    get startTimePeriod() {
        return this._attributes.startTimePeriod;
    }
    set startTimePeriod(value) {
        this._attributes.startTimePeriod = value;
    }
    get endDate() {
        return this._attributes.endDate;
    }
    set endDate(value) {
        this._attributes.endDate = value;
    }
    get endTime() {
        return this._attributes.endTime;
    }
    set endTime(value) {
        this._attributes.endTime = value;
    }
    get endTimePeriod() {
        return this._attributes.endTimePeriod;
    }
    set endTimePeriod(value) {
        this._attributes.endTimePeriod = value;
    }
    get parkingType() {
        return this._attributes.parkingType;
    }
    set parkingType(value) {
        this._attributes.parkingType = value;
    }
    get parkingFeeType() {
        return this._attributes.parkingFeeType;
    }
    set parkingFeeType(value) {
        this._attributes.parkingFeeType = value;
    }
    get transitPlaceType() {
        return this._attributes.transitPlaceType;
    }
    set transitPlaceType(value) {
        this._attributes.transitPlaceType = value;
    }
    get preData() {
        return this._attributes.preData;
    }
    set preData(value) {
        this._attributes.preData = value;
    }
    get tripUuid() {
        return this._tripUuid;
    }
    set tripUuid(value) {
        this._tripUuid = value;
    }
    get trip() {
        if (!this._tripUuid) {
            return undefined;
        }
        return this._surveyObjectsRegistry.getTrip(this._tripUuid);
    }
    /**
     * Creates a Junction object from sanitized parameters
     * @param {ExtendedJunctionAttributes | SerializedExtendedJunctionAttributes} params - Sanitized junction parameters
     * @returns {Junction} New Junction instance
     */
    static unserialize(params, surveyObjectsRegistry) {
        const flattenedParams = SurveyObjectUnserializer_1.SurveyObjectUnserializer.flattenSerializedData(params);
        return new Junction(flattenedParams, surveyObjectsRegistry);
    }
    static create(dirtyParams, surveyObjectsRegistry) {
        const errors = Junction.validateParams(dirtyParams);
        const junction = errors.length === 0 ? new Junction(dirtyParams, surveyObjectsRegistry) : undefined;
        if (errors.length > 0) {
            return (0, Result_type_1.createErrors)(errors);
        }
        return (0, Result_type_1.createOk)(junction);
    }
    /**
     * Validates attributes types for Junction.
     * @param dirtyParams The parameters to validate.
     * @param displayName The name of the object to validate, for error display
     * @returns Error[]
     */
    static validateParams(dirtyParams, displayName = 'Junction') {
        const errors = [];
        // Validate params object:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRequired('params', dirtyParams, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('params', dirtyParams, displayName));
        // Validate _uuid:
        errors.push(...Uuidable_1.Uuidable.validateParams(dirtyParams));
        // Validate _isValid:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isBoolean('_isValid', dirtyParams._isValid, displayName));
        errors.push(...SurveyObject_1.SurveyObject.validateCompletableParams(dirtyParams, displayName));
        // Validate _weights:
        errors.push(...(0, Weight_1.validateWeights)(dirtyParams._weights));
        // Validate StartEndable attributes:
        errors.push(...StartEndable_1.StartEndable.validateParams(dirtyParams, displayName));
        // Validate junction-specific attributes:
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('parkingType', dirtyParams.parkingType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('parkingFeeType', dirtyParams.parkingFeeType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('transitPlaceType', dirtyParams.transitPlaceType, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isRecord('preData', dirtyParams.preData, displayName, false));
        // Validate composed place:
        const placeAttributes = dirtyParams._place;
        if (placeAttributes) {
            errors.push(...Place_1.Place.validateParams(placeAttributes, 'Junction Place'));
        }
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isUuid('_tripUuid', dirtyParams._tripUuid, displayName));
        return errors;
    }
}
exports.Junction = Junction;
Junction._confidentialAttributes = ['preData'];
//# sourceMappingURL=Junction.js.map